import { applyCartCoupon, deleteCart, updateCart } from '@/api/cart';
import { ICartItem } from '@/api/types/cart';
import { fetchAddWish, fetchCancelWish } from '@/api/wish';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { toast } from '@/components/Toast';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import logger from '@/utils/client-logger';
import { DIRECTLY_ADD_TO_CART_PARAMS } from '@/utils/constants';
import { EPrescriptionType } from '@/utils/enums/goods';
import { EPlatform } from '@/utils/enums/platform-enum';
import { setBlockFlags } from '@/utils/feature-value';
import { handlerPrescriptionData } from '@/utils/prescription';
import { setSession } from '@/utils/tools';
import { useRouter } from 'next/navigation';
import { useCallback, useContext, useRef, useState } from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { useCartGaTracking } from '../../hooks/useCartGaTracking';
import { useCartSensorsTracking } from '../../hooks/useCartSensorsTracking';
import { useCartThirdPartyTracking } from '../../hooks/useCartThirdPartyTracking';
import { usePriceCalculation } from './usePriceCalculation';
import { useTranslations } from '@/i18n/use-translations';
// 类型定义
interface UseCartItemProps {
  cartItemData: ICartItem;
  batchFlag: boolean;
  onReload: (cartData?: any, modifiedCartId?: number) => void;
  selected?: boolean;
  onSelectChange?: (selected: boolean) => void;
  itemPromotion?: any;
  currency?: any;
  itemType?: number;
  // 不可结算商品选中回调
  onInvalidSelectChange?: (checked: boolean) => void;
  // 赠品相关属性
  giftCountDown?: number; // 赠品倒计时时间
  giftAllowType?: number; // 赠品是否可结算 1可结算 giftCountDown为可计算结束时间倒计时  2不可结算 giftCountDown为可计算开始时间倒计时
  itemGiftInfo?: any; // 独立赠品的intro提示
}

export const useCartItem = ({
  cartItemData,
  batchFlag,
  onReload,
  selected = false,
  onSelectChange,
  itemPromotion,
  currency,
  itemType = 1,
  onInvalidSelectChange,
  // 赠品相关属性
  giftCountDown = 0,
  giftAllowType = 0,
  itemGiftInfo = {},
}: UseCartItemProps) => {
  const t = useTranslations('cart.hooks');
  const globalT = useTranslations('cart');
  // 获取设备类型
  const { deviceType } = useContext(ResponsiveContext);
  const router = useRouter();
  // 状态管理
  const [loading, setLoading] = useState(false);
  const [showQtyOperationBtn, setShowQtyOperationBtn] = useState(false);
  const [showChangeColor, setShowChangeColor] = useState(false);
  const [showCartDetailPopup, setShowCartDetailPopup] = useState(false);
  const [colorPopoverAnchor, setColorPopoverAnchor] = useState<HTMLElement | null>(null);
  const [showCartItemDialog, setShowCartItemDialog] = useState(false);
  const [cartItemDialogProps, setCartItemDialogProps] = useState<any>({});
  const [showLimitBuyDialog, setShowLimitBuyDialog] = useState(false);
  // 兑换确认弹窗状态
  const [showRedeemedConfirmDialog, setShowRedeemedConfirmDialog] = useState(false);
  // 配镜弹窗状态 - 迁移到本地管理（参考useCartDialogs的实现）
  const [showLensProcessDialog, setShowLensProcessDialog] = useState(false);
  const [lensProcessCartItemData, setLensProcessCartItemData] = useState<any>({});
  const swiperRef = useRef<SwiperType | null>(null);
  const [showPreCountDown, setShowPreCountDown] = useState(true);
  const [afterAnniGreaterFlag, setAfterAnniGreaterFlag] = useState(false);
  const { trackEvent } = useReporting();

  // 获取store中的状态
  const token = useStore((state) => state.token);
  const setShowLoginRegisterDialog = useStore((state) => state.setShowLoginRegisterDialog);
  const exchangeCouponList = useStore((state) => state.exchangeCouponList);
  const wishNum = useStore((state) => state.wishNum);
  const setWishNum = useStore((state) => state.setWishNum);
  const userInfo = useStore((state) => state.userInfo);
  const visitorId = useStore((state) => state.visitorId);
  const setIsGlobalLoading = useStore((state) => state.setIsGlobalLoading);
  // 埋点相关Hook
  const {
    trackCartPrescriptionEditBtnClick,
    trackCartItemBtnClick,
    trackCollectBtnClick,
    trackCollectClick,
    trackCartFunctionBtnClick,
  } = useCartSensorsTracking();

  // GA埋点相关Hook
  const { trackQuantityChange, trackCartEdit } = useCartGaTracking();

  // 第三方埋点相关Hook
  const { trackFacebookAddToWishlist } = useCartThirdPartyTracking();

  // 移除对全局cartDialogs的依赖
  // const cartDialogs = useCartDialogs();

  // 使用统一的价格计算hook
  const priceCalculation = usePriceCalculation({
    cartItemData,
    currency,
    selected,
    itemPromotion,
  });

  // 获取商品图片
  const getProductImage = () => {
    return cartItemData.product?.img_gray || cartItemData.product?.img || '';
  };

  const isGlassesGrayImage =
    (cartItemData.product?.img_gray && cartItemData.product?.img_gray !== '') ||
    cartItemData.product?.is_glass !== 1;

  // 获取商品名称
  const getProductName = () => {
    return cartItemData.product?.short_name || '';
  };

  // 显示降价信息（从价格计算hook获取）
  const showDownPrice = () => {
    return priceCalculation.showDownPrice();
  };

  // 获取降价金额（从价格计算hook获取）
  const getDownPrice = () => {
    return priceCalculation.getDownPrice();
  };

  /**
   * 生成TikTok事件ID
   * - 有token时：userInfo.id
   * - 无token时：visitorId
   */
  const generateTtqEventId = useCallback(
    (shopSku?: string) => {
      const baseId = token ? userInfo.id : visitorId;
      return shopSku ? `${baseId}${shopSku}` : baseId || '';
    },
    [token, visitorId],
  );

  // 商品状态判断
  const outStock = cartItemData.stock < 1 || cartItemData.product.status === 2;
  const lensCanotUse =
    ((cartItemData.product.status === 1 && cartItemData.stock >= 1) || itemType === 4) &&
    cartItemData.lens &&
    cartItemData.lens.is_match !== 1;
  const editShow =
    cartItemData.product.is_glass !== 2 &&
    cartItemData.option.prescription_type !== EPrescriptionType.FRAME_ONLY;

  // 判断商品是否可以兑换 (对应Vue的canUseRedeemCouponToRedeem)
  const canUseRedeemCouponToRedeem = useCallback(() => {
    const rule1 = !lensCanotUse; // 镜片可用
    const rule2 = itemType === 1; // 普通商品
    const rule3 = (cartItemData?.product as any)?.is_exchange === 1; // 商品可兑换
    const rule4 = !outStock; // 有库存
    const rule5 = Array.isArray(exchangeCouponList) && exchangeCouponList.length > 0; // 有兑换券

    return rule1 && rule2 && rule3 && rule4 && rule5;
  }, [lensCanotUse, itemType, cartItemData?.product, outStock, exchangeCouponList]);

  // 执行兑换
  const handleRedemption = useCallback(async () => {
    try {
      if (!exchangeCouponList || exchangeCouponList.length === 0) {
        toast.error(t('noAvailableExchangeCoupons'));
        return;
      }

      const param = {
        cart_id: cartItemData.cart_id,
        coupon_id: (exchangeCouponList[0].relationable as any)?.id || 0,
      };

      await applyCartCoupon(param);
      // 重新加载购物车数据
      onReload();
    } catch (error) {
      logger.error('Failed to redeem:', error);
      toast.error(t('exchangeFailed'));
    }
  }, [exchangeCouponList, cartItemData.cart_id, onReload, t]);

  // 显示兑换确认弹窗
  const showRedemptionDialog = useCallback(() => {
    if (!exchangeCouponList || exchangeCouponList.length === 0) {
      toast.error(t('noAvailableExchangeCoupons'));
      return;
    }

    setShowRedeemedConfirmDialog(true);
  }, [exchangeCouponList, t]);

  // 处理兑换确认
  const handleRedeemedConfirm = useCallback(async () => {
    await handleRedemption();
  }, [handleRedemption]);

  // 处理兑换取消
  const handleRedeemedCancel = useCallback(() => {
    setShowRedeemedConfirmDialog(false);
  }, []);

  // 显示取消兑换确认弹窗
  const showCancelRedemptionDialog = useCallback(() => {
    const dialogProps = {
      content: t('cancelRedemptionConfirm'),
      content2: t('cancelRedemptionTip'),
      confirmText: 'YES',
      cancelText: 'NO',
      cancelRedeeme: 1, // 标记为取消兑换样式
      onConfirm: () => {
        handleCancelRedemption();
        setShowCartItemDialog(false);
      },
      onCancel: () => {
        setShowCartItemDialog(false);
      },
    };

    setCartItemDialogProps(dialogProps);
    setShowCartItemDialog(true);
  }, []);

  // 执行取消兑换
  const handleCancelRedemption = useCallback(async () => {
    try {
      const param = {
        cart_id: cartItemData.cart_id,
        coupon_id: 0, // 设为0表示取消兑换
      };

      const response = await applyCartCoupon(param);

      if (response && (response as any).data?.cart_ids) {
        const cartIds = (response as any).data.cart_ids;
        if (Array.isArray(cartIds) && cartIds.length > 0) {
          // 保存新添加的商品ID到sessionStorage
          sessionStorage.setItem('zl-cartPageEditAdd', String(cartIds[0]));
        }
      }

      // 重新加载购物车数据
      onReload();
    } catch (error) {
      logger.error('Failed to cancel redemption:', error);
      toast.error(t('failedToCancelRedemption'));
    }
  }, [cartItemData.cart_id, onReload, t]);

  // 促销相关计算
  const flag1 =
    selected &&
    itemPromotion &&
    itemPromotion.products_promotions_show === 1 &&
    itemPromotion.products_promotions_qty > 0 &&
    [2, 3].includes(Number(itemPromotion.products_promotions_type));

  const flag2 =
    selected &&
    itemPromotion &&
    itemPromotion.products_promotions_show === 2 &&
    [2, 3].includes(Number(itemPromotion.products_promotions_type));

  // 更新数量
  const qtyChange = async (item: any, type: 'reduce' | 'add') => {
    if (item.qty <= 1 && type === 'reduce') return;
    if (item.qty >= 5 && type === 'add') return;
    if (item.qty >= cartItemData.stock && type === 'add') return;

    // 🔍 发送神策埋点 - 数量变化
    trackCartFunctionBtnClick('Qty');

    setLoading(true);

    try {
      if (type === 'reduce') {
        item.qty--;
        if (item.qty > cartItemData.stock) {
          item.qty = cartItemData.stock;
        }
        if (item.qty > 5) {
          item.qty = 5;
        }
      } else if (type === 'add') {
        ++item.qty;
      }

      const params = handlerPrescriptionData({
        cart_id: cartItemData.cart_id,
        product_id: cartItemData.product_id,
        qty: item.qty,
        option: {
          ...cartItemData.option,
        },
        prescriptions_image: cartItemData.prescriptions_image || '',
      });

      const response = await updateCart(params);

      if (response?.data) {
        // 🔍 发送GA埋点 - 数量变更成功
        trackQuantityChange();

        // 🔍 发送TikTok埋点 - AddToCart
        trackEvent(
          undefined,
          [ProviderType.TIKTOK],
          {
            lensGoodsItem: cartItemData,
            // Tiktok暂时没用到option.precription_type
            requestData: { currency: currency?.name, option: { prescription_type: '' } },
            quantity: item.qty,
            ttqEventId: generateTtqEventId(cartItemData.product.shop_sku),
          },
          'addToCart.success',
        );

        onReload(response.data, cartItemData.cart_id);
      }
    } catch (error) {
      logger.error('Failed to update quantity:', error);
    } finally {
      setLoading(false);
      trackCartItemBtnClick(cartItemData as any, type === 'add' ? 'add_product' : 'less_product', {
        selected,
        itemPromotion,
      });
    }
  };

  // 删除商品
  const handleDelete = () => {
    if (!cartItemData.cart_id) return;

    const dialogProps: any = {
      content2: t('deleteItemConfirm'),
      confirmText: t('delete'),
      cancelText: t('cancel'),
      removeLens: false,
      onConfirm: () => {
        deleteItem(cartItemData);
        closeSwiper();
      },
      onRemoveLens: () => {},
    };

    if (itemType === 4) {
      dialogProps.content = t('removeGiftConfirm');
      dialogProps.content2 = t('removeGiftTip');
      dialogProps.content2TextAlignCenter = true;
    }

    setCartItemDialogProps(dialogProps);
    setShowCartItemDialog(true);
  };

  // 确认删除商品
  const deleteItem = async (cartItem: ICartItem) => {
    try {
      setLoading(true);
      await deleteCart({ cart_ids: [cartItem.cart_id] });

      // 🔍 发送神策埋点 - 删除商品
      trackCartItemBtnClick(cartItem as any, 'item删除', { selected, itemPromotion });

      onReload();
    } catch (error) {
      logger.error('Failed to delete item:', error);
    } finally {
      setLoading(false);
    }
  };

  // 移动到心愿单
  const handleMoveToWishlist = () => {
    if (!token) {
      setShowLoginRegisterDialog(true);
      return;
    }

    if (cartItemData.product.is_wish === 1 && deviceType === EPlatform.WEB) {
      removeFromWishlist(cartItemData);
    } else {
      moveToWish(cartItemData);
    }
  };

  // 移至心愿单的具体实现
  const moveToWish = async (cartItem: ICartItem) => {
    try {
      setLoading(true);

      // 🔍 发送神策埋点 - 收藏按钮点击和收藏点击
      trackCollectBtnClick(cartItem as any);
      trackCollectClick(cartItem as any);

      const response = await fetchAddWish({ product_id: cartItem.product_id });

      if (response.code === 200) {
        cartItem.product.is_wish = 1;
        toast.success(t('successAddToWishlist'));

        // 🔍 发送Facebook埋点 - AddToWishlist
        trackFacebookAddToWishlist(cartItem as any);

        // 更新心愿单数量
        setWishNum(wishNum + 1);
      }
    } catch (error) {
      logger.error('Failed to add to wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  // 移除收藏的具体实现
  const removeFromWishlist = async (cartItem: ICartItem) => {
    try {
      setLoading(true);

      // 🔍 发送神策埋点 - 取消收藏按钮点击和收藏点击
      trackCollectBtnClick(cartItem as any); // isBatchAction = false (单个操作)
      trackCollectClick(cartItem as any);

      const response = await fetchCancelWish({ product_id: [cartItem.product_id] });

      if (response.code === 200) {
        cartItem.product.is_wish = 2;
        toast.success(t('successRemoveFromWishlist'));

        // 更新心愿单数量
        setWishNum(wishNum - 1);
      }
    } catch (error) {
      logger.error('Failed to remove from wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  // 编辑处方
  const handleEditPrescription = () => {
    // 🔍 发送神策埋点 - 购物车处方编辑按钮点击
    handleCartPrescriptionEditBtnClick('Edit', cartItemData);

    // 🔍 发送GA埋点 - cart_edit
    trackCartEdit();
  };

  // 添加镜片
  const handleAddLens = () => {
    // 🔍 发送神策埋点 - 购物车处方编辑按钮点击
    handleCartPrescriptionEditBtnClick('Select Lenses', cartItemData);

    // 🔍 发送GA埋点 - cart_edit
    trackCartEdit();
  };

  // 移除镜片
  const handleRemoveLens = async () => {
    try {
      setLoading(true);
      setIsGlobalLoading(true);

      const params = handlerPrescriptionData({
        cart_id: cartItemData.cart_id,
        product_id: cartItemData.product_id,
        qty: cartItemData.qty,
        option: {
          ...DIRECTLY_ADD_TO_CART_PARAMS,
          prescription_type: EPrescriptionType.FRAME_ONLY,
        },
        prescriptions_image: cartItemData.prescriptions_image || '',
      });

      const response = await updateCart(params);

      if (response?.data) {
        onReload(response.data, cartItemData.cart_id);
      }
      setIsGlobalLoading(false);
      handleCartDetailPopupClose();
      handleCartPrescriptionEditBtnClick('Remove lenses', cartItemData);
    } catch (error) {
      logger.error('Error removing lens:', error);
    } finally {
      setLoading(false);
    }
  };

  // 选择状态改变（勾选埋点在 cart page 的 selectChange）
  const handleSelectChange = (checked: boolean) => {
    onSelectChange?.(checked);
  };

  // 不可结算商品选中状态改变
  const handleInvalidSelectChange = (checked: boolean) => {
    onInvalidSelectChange?.(checked);
  };

  // 关闭当前swiper
  const closeSwiper = () => {
    if (swiperRef.current) {
      swiperRef.current.slideTo(0, 500, false);
    }
  };

  // 处理swiper滑动时关闭其他swiper
  const handleSliderMove = () => {};

  // 获取促销商品文本
  const getPromotionItemText = (qty: number) => {
    if (cartItemData.product.is_glass === 1) {
      return qty > 1 ? globalT('cartItem.frames') : globalT('cartItem.frame');
    }
    return qty > 1 ? globalT('cartItem.items') : globalT('cartItem.item');
  };

  // 检查是否显示超库存提示
  const shouldShowOverStockWarning = () => {
    if (!outStock && !lensCanotUse && itemType === 1) {
      if (selected && itemPromotion) {
        return itemPromotion.qty > cartItemData.stock;
      }
      return cartItemData.qty > cartItemData.stock;
    }
    return false;
  };

  // 是否可以更换颜色
  const canChangeColorClick = () => {
    if (itemType !== 1) return false;
    if (cartItemData.product?.is_glass !== 1) return false;
    if (
      !Array.isArray(cartItemData.product?.same_color_spu) ||
      cartItemData.product.same_color_spu.length < 2
    ) {
      return false;
    }
    return true;
  };

  // 点击更换颜色
  const handleChangeColorClick = (event?: React.MouseEvent<HTMLElement>) => {
    if (batchFlag) return;
    if (!canChangeColorClick()) return;
    if (event) {
      setColorPopoverAnchor(event.currentTarget);
    }
    setShowChangeColor(true);
  };

  // 关闭颜色弹窗
  const handleColorPopupClose = () => {
    setShowChangeColor(false);
    setColorPopoverAnchor(null);
  };

  // 处理处方类型点击
  const handlePrescriptionTypeClick = () => {
    if (batchFlag) return;
    setShowCartDetailPopup(!showCartDetailPopup);
  };

  // 关闭购物车详情弹窗
  const handleCartDetailPopupClose = () => {
    setShowCartDetailPopup(false);
  };

  const handleOpenLimitBuyDialog = () => {
    if (batchFlag) return;
    setShowLimitBuyDialog(true);
  };

  // 处理倒计时结束
  const handleCountDownEnd = () => {
    setShowPreCountDown(false);
  };

  // 处理afterAnniGreaterFlag变化
  const handleChangeAfterAnniGreaterFlag = (flag: boolean) => {
    setAfterAnniGreaterFlag(flag);
  };

  // 获取intro提示文本
  const getIntroText = () => {
    return itemGiftInfo?.intro || '';
  };

  // 处理商品图片点击跳转详情
  const handleProductImageClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault(); // 阻止a标签默认跳转

      // 在批量模式下阻止跳转
      if (batchFlag) {
        return;
      }

      // 🔍 发送神策埋点 - 商品图片点击跳转详情
      trackCartItemBtnClick(cartItemData as any, 'Product', { selected, itemPromotion });

      if (cartItemData.product?.shop_sku) {
        setSession('zl_goodsDetailEnterPageScene', '购物车页商品条目');
        router.push(`/goods-detail/${cartItemData.product.shop_sku}`);
        setBlockFlags({
          refer_page_position: '购物车条目商品',
        });
      }
    },
    [cartItemData, trackCartItemBtnClick, batchFlag, selected, itemPromotion],
  );

  // 处方编辑按钮点击事件 - 在hooks内部处理，使用全局cartDialogs管理对话框
  const handleCartPrescriptionEditBtnClick = useCallback(
    (btnName: string, itemData: ICartItem) => {
      // 🔍 发送神策埋点 - 处方编辑按钮点击
      trackCartPrescriptionEditBtnClick(itemData as any, btnName);

      // 处理对话框逻辑 - 使用全局cartDialogs
      if (['Edit', 'Select Lenses'].includes(btnName)) {
        setLensProcessCartItemData(itemData);
        setShowLensProcessDialog(true);
        setShowCartItemDialog(false);
        sessionStorage.setItem(
          'zl-sensors-lens-entrance-scene',
          `${btnName === 'Edit' ? '购物车处方编辑' : '购物车添加镜片编辑'}|Select Lenses`,
        );
        if (deviceType === EPlatform.WAP) {
          handleCartDetailPopupClose();
        }
      }
    },
    [trackCartPrescriptionEditBtnClick],
  );

  // 关闭配镜弹窗
  const handleLensProcessDialogClose = useCallback(() => {
    setShowLensProcessDialog(false);
    setLensProcessCartItemData({});
  }, []);

  // 返回所有方法和状态
  return {
    // 状态
    deviceType,
    loading,
    showQtyOperationBtn,
    setShowQtyOperationBtn,
    showChangeColor,
    showCartDetailPopup,
    colorPopoverAnchor,
    showCartItemDialog,
    setShowCartItemDialog,
    cartItemDialogProps,
    showLimitBuyDialog,
    setShowLimitBuyDialog,
    swiperRef,
    showPreCountDown,
    afterAnniGreaterFlag,

    // 计算属性
    outStock,
    lensCanotUse,
    editShow,
    flag1,
    flag2,
    isGlassesGrayImage,

    // 赠品相关属性
    giftCountDown,
    giftAllowType,
    itemGiftInfo,

    // 方法
    formatPrice: priceCalculation.formatPrice,
    getProductImage,
    getProductName,
    getPriceByCurrency: priceCalculation.getPriceByCurrency,
    getAllPrices: priceCalculation.getAllPrices,
    getLensPrice: priceCalculation.getLensPrice,
    getIndexPrice: priceCalculation.getIndexPrice,
    getCoatingPrice: priceCalculation.getCoatingPrice,
    getPrescriptionPrice: priceCalculation.getPrescriptionPrice,
    getItemTotalPrice: priceCalculation.getItemTotalPrice,
    showDownPrice,
    getDownPrice,
    qtyChange,
    handleDelete,
    deleteItem,
    handleMoveToWishlist,
    handleEditPrescription,
    handleAddLens,
    handleRemoveLens,
    handleSelectChange,
    handleInvalidSelectChange,
    closeSwiper,
    handleSliderMove,
    getPromotionItemText,
    shouldShowOverStockWarning,
    canChangeColorClick,
    handleChangeColorClick,
    handleColorPopupClose,
    handlePrescriptionTypeClick,
    handleCartDetailPopupClose,
    handleOpenLimitBuyDialog,
    handleCountDownEnd,
    handleChangeAfterAnniGreaterFlag,
    getIntroText,
    handleProductImageClick,
    // 新增：包装onReload为onReloadCart，供BlueLightItem等组件使用
    onReloadCart: onReload,

    // 兑换相关方法和数据
    exchangeCouponList,
    canUseRedeemCouponToRedeem,
    showRedemptionDialog,
    handleRedemption,
    showCancelRedemptionDialog,
    handleCancelRedemption,
    // 兑换确认弹窗
    showRedeemedConfirmDialog,
    handleRedeemedConfirm,
    handleRedeemedCancel,

    // 镜片处方弹窗状态
    showLensProcessDialog,
    lensProcessCartItemData,
    handleCartPrescriptionEditBtnClick,
    handleLensProcessDialogClose,
  };
};
